import { Button } from '@startengine/design-system.ui.button';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useState } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { colors } from '@startengine/design-system.theme.main';
import Countdown from 'react-countdown';

import { useSelector } from 'react-redux';
import { ButtonGrid, LowerBox, RewardTile, TextGrid, TimeGrid, UpperBox } from './styles';
import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useLoginModal from 'hooks/useLoginModal';
import { formatCurrencyFlexible, getEnv } from 'utils/helpers';

const RewardTiles = ({ reward = {} }) => {
  const queryClient = useQueryClient();
  const offeringData = queryClient.getQueryData(['offeringData']);
  const [selected, setSelected] = useState(false);
  const isMobile = useIsMobile();
  const isEarlyBird = Boolean(!reward?.neverEnding);
  const futureDate = isEarlyBird && new Date(reward?.endDate).getTime();
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerVerificationLoading,
  } = useSelector((state) => state.partnerVerification);
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [url, setUrl] = useState('');
  const signedIn = useIsSignedIn();

  const handleClick = (amount) => {
    const rewardAmount = amount?.substring(1)?.replace(',', '');
    const url = `${`${getEnv()}investment`}.startengine.com/${
      offeringData?.slug
    }?amt=${rewardAmount}`;

    if (!signedIn || (needToLogin && !partnerVerificationLoading)) {
      setUrl(url);
      setLoginModalOpen(true);
      return;
    }

    window.location.href = url;

    setSelected(false);
  };

  const getMaxHeight = () => {
    if (isMobile) {
      return null;
    }

    if (isEarlyBird) {
      return '130px';
    }

    return '178px';
  };

  const countDownRenderer = ({ days, hours, minutes, seconds }) => {
    return (
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          position: isMobile ? 'relative' : 'absolute',
          bottom: isMobile ? '-5px' : '50px',
          width: '200px',
        }}
      >
        <TimeGrid>
          <Typography variant="h7" color={colors.SEOrange}>
            {days < 10 ? `0${days}` : days}
          </Typography>
          <Typography variant="p3SemiBold" color={colors.SEOrange}>
            Days
          </Typography>
        </TimeGrid>
        <TimeGrid>
          <Typography variant="h7" color={colors.SEOrange}>
            {hours < 10 ? `0${hours}` : hours}
          </Typography>
          <Typography variant="p3SemiBold" color={colors.SEOrange}>
            Hours
          </Typography>
        </TimeGrid>
        <TimeGrid>
          <Typography variant="h7" color={colors.SEOrange}>
            {minutes < 10 ? `0${minutes}` : minutes}
          </Typography>
          <Typography variant="p3SemiBold" color={colors.SEOrange}>
            Minutes
          </Typography>
        </TimeGrid>
        <TimeGrid>
          <Typography variant="h7" color={colors.SEOrange}>
            {seconds < 10 ? `0${seconds}` : seconds}
          </Typography>
          <Typography variant="p3SemiBold" color={colors.SEOrange}>
            Seconds
          </Typography>
        </TimeGrid>
      </Grid>
    );
  };

  return (
    <>
      <RewardTile
        onClick={() => handleClick(formatCurrencyFlexible(parseFloat(reward?.minimumAmount)))}
        key={reward?.title}
      >
        <UpperBox container>
          <Typography variant="h3" color={colors.SEDark}>
            {reward?.minimumAmount && formatCurrencyFlexible(parseFloat(reward?.minimumAmount))}
          </Typography>
          {isMobile && (
            <Button
              data-cy={`rewards-${reward?.title}-desktop-select-button`}
              variant={selected ? 'contained' : 'outlined'}
              color="primary"
              customHeight="32px"
              customWidth="90px"
              sx={{
                borderRadius: '8px',
                height: '32px',
                width: '90px',
              }}
              onClick={() => handleClick(formatCurrencyFlexible(parseFloat(reward?.minimumAmount)))}
            >
              {selected ? 'Selected' : 'Select'}
            </Button>
          )}
        </UpperBox>
        <LowerBox>
          <TextGrid container sx={{ maxHeight: getMaxHeight() }}>
            <Typography
              variant="p1Medium"
              color={isEarlyBird ? colors.SEOrange : colors.SEDark}
              mb={0.5}
            >
              {reward?.title
                .replace(/[^a-zA-Z0-9}{><^,+|/%$-\u3164 ]/g, '')
                .replace(/\u3164/g, ' ')}
            </Typography>
            {/<[a-z][\s\S]*>/i.test(reward.description) ? (
              <Typography
                variant="p3"
                color={isEarlyBird ? colors.SEOrange : colors.SEBlack}
                pb={1.5}
                dangerouslySetInnerHTML={{ __html: reward.description }}
              />
            ) : (
              <Typography
                variant="p3"
                color={isEarlyBird ? colors.SEOrange : colors.SEBlack}
                pb={1.5}
              >
                {reward.description}
              </Typography>
            )}
          </TextGrid>
          {isEarlyBird && <Countdown date={futureDate} renderer={countDownRenderer} />}
          {!isMobile && (
            <ButtonGrid>
              <Button
                data-cy={`rewards-${reward?.title}-mobile-select-button`}
                variant={selected ? 'contained' : 'outlined'}
                color="primary"
                customHeight="32px"
                customWidth="90px"
                onClick={() =>
                  handleClick(formatCurrencyFlexible(parseFloat(reward?.minimumAmount)))
                }
              >
                {selected ? 'Selected' : 'Select'}
              </Button>
            </ButtonGrid>
          )}
        </LowerBox>
      </RewardTile>
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onSuccessfulLogin: () => {
          window.location.href = url;
          setSelected(false);
        },
      })}
    </>
  );
};

export default RewardTiles;

RewardTiles.propTypes = {
  reward: PropTypes.object,
};
